import React, { useCallback, useEffect, useId, useReducer, useRef, useState } from 'react';
import {createPortal} from 'react-dom';

export function tooltipVisibilityReducer(isOpen, action) {
  switch (action.type) {
    case 'open':
      return true;
    case 'activate':
      return action.keyboard ? true : !action.wasOpen;
    case 'close':
      return false;
    default:
      return isOpen;
  }
}

function Tooltip({
  children,
  className,
  bg,
  size,
  position,
  trigger,
  triggerClassName = '',
  ariaLabel = 'More information',
  ariaPressed,
  onClick,
}) {

  const [tooltipOpen, dispatchTooltip] = useReducer(tooltipVisibilityReducer, false);
  const tooltipId = useId();
  const triggerRef = useRef(null);
  const [portalStyle, setPortalStyle] = useState(null);
  const pointerDownWasOpen = useRef(false);
  const pointerDownType = useRef(null);

  const tooltipWidth = () => {
    switch (size) {
      case 'lg':
        return 384;
      case 'md':
        return 320;
      case 'sm':
        return 224;
      default:
        return 240;
    }
  };

  const updatePortalPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.min(tooltipWidth(), window.innerWidth - 32);
    const centeredLeft = rect.left + rect.width / 2 - width / 2;
    const left = Math.max(16, Math.min(centeredLeft, window.innerWidth - width - 16));

    switch (position) {
      case 'right':
        setPortalStyle({left: Math.min(rect.right + 8, window.innerWidth - width - 16), top: rect.top + rect.height / 2, width, transform: 'translateY(-50%)'});
        break;
      case 'left':
        setPortalStyle({left: Math.max(16, rect.left - width - 8), top: rect.top + rect.height / 2, width, transform: 'translateY(-50%)'});
        break;
      case 'bottom':
        setPortalStyle({left, top: rect.bottom + 8, width});
        break;
      default:
        setPortalStyle({left, bottom: window.innerHeight - rect.top + 8, width});
    }
  }, [position, size]);

  useEffect(() => {
    if (!tooltipOpen) return undefined;

    const closeOnOutsidePointer = (event) => {
      if (!triggerRef.current?.contains(event.target)) {
        dispatchTooltip({type: 'close'});
      }
    };

    updatePortalPosition();
    window.addEventListener('resize', updatePortalPosition);
    window.addEventListener('scroll', updatePortalPosition, true);
    window.addEventListener('pointerdown', closeOnOutsidePointer);

    return () => {
      window.removeEventListener('resize', updatePortalPosition);
      window.removeEventListener('scroll', updatePortalPosition, true);
      window.removeEventListener('pointerdown', closeOnOutsidePointer);
    };
  }, [tooltipOpen, updatePortalPosition]);

  const sizeClasses = (size) => {
    switch (size) {
      case 'lg':
        return 'w-96 max-w-[calc(100vw-2rem)] px-3 py-2';
      case 'md':
        return 'w-80 max-w-[calc(100vw-2rem)] px-3 py-2';
      case 'sm':
        return 'w-56 max-w-[calc(100vw-2rem)] px-3 py-2';
      default:
        return 'px-3 py-2';
    }
  };

  const colorClasses = (bg) => {
    switch (bg) {
      case 'light':
        return 'bg-white text-gray-600 border-gray-200';
      case 'dark':
        return 'bg-gray-800 text-gray-100 border-gray-700/60';
      default:
        return 'text-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700/60';
    }
  };

  return (
    <div
      ref={triggerRef}
      className={`relative ${className}`}
      onPointerEnter={(event) => { if (event.pointerType === 'mouse') dispatchTooltip({type: 'open'}); }}
      onPointerLeave={(event) => { if (event.pointerType === 'mouse') dispatchTooltip({type: 'close'}); }}
      onFocus={() => dispatchTooltip({type: 'open'})}
      onBlur={() => dispatchTooltip({type: 'close'})}
    >
      <button
        type="button"
        className={`${trigger ? "inline-flex" : "block"} ${triggerClassName}`}
        aria-expanded={tooltipOpen}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        aria-describedby={tooltipOpen ? tooltipId : undefined}
        onPointerDown={(event) => { pointerDownWasOpen.current = tooltipOpen; pointerDownType.current = event.pointerType; }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            dispatchTooltip({type: 'close'});
          }
        }}
        onClick={(event) => {
          event.preventDefault();
          const keyboard = event.detail === 0;
          if (keyboard || pointerDownType.current === 'mouse') {
            dispatchTooltip({type: 'open'});
          } else {
            dispatchTooltip({type: 'activate', keyboard: false, wasOpen: pointerDownWasOpen.current});
          }
          onClick?.();
        }}
      >
        {trigger ?? (
          <svg className="shrink-0 fill-current opacity-40" width="16" height="16" viewBox="0 0 16 16">
            <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 12H7V7h2v5zM8 6c-.6 0-1-.4-1-1s.4-1 1-1s1 .4 1 1-.4 1-1 1z"></path>
          </svg>
        )}
      </button>
      {tooltipOpen && portalStyle && createPortal(<div className="pointer-events-none fixed z-[1000]" style={portalStyle}>
        <div id={tooltipId} role="tooltip" className={`whitespace-normal break-words rounded-lg border overflow-hidden shadow-lg ${sizeClasses(size)} ${colorClasses(bg)}`}>
          {children}
        </div>
      </div>, document.body)}
    </div>
  );
}

export default Tooltip;
